// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * hex.ts — axial hex geometry. Pure, no DOM, no state.
 *
 * Pointy-top hexes in axial coordinates (q, r). The map is the set of cells
 * within `radius` of the origin, which is where the generator always stands, so
 * "distance from the generator" is just `dist(cell, ORIGIN)` and the warmth
 * radius is a plain integer compared against it. That is the whole reason the
 * map is a hex disc rather than a rectangle: the warmth ring is a *circle*, and
 * on a hex grid a circle is exactly a ring of constant distance. On a square
 * grid it would be an approximation the player could see and argue with.
 */

export interface Cell {
  readonly q: number;
  readonly r: number;
}

export const ORIGIN: Cell = { q: 0, r: 0 };

/** Pack an axial coord into a single integer key. Range is far beyond any map we build. */
export function key(q: number, r: number): number {
  return (q + 64) * 256 + (r + 64);
}

export function unkey(k: number): Cell {
  return { q: Math.floor(k / 256) - 64, r: (k % 256) - 64 };
}

/** The six axial directions, in a fixed order so iteration is deterministic. */
export const DIRECTIONS: readonly Cell[] = [
  { q: 1, r: 0 },
  { q: 1, r: -1 },
  { q: 0, r: -1 },
  { q: -1, r: 0 },
  { q: -1, r: 1 },
  { q: 0, r: 1 },
];

/** Hex distance in axial space. */
export function dist(a: Cell, b: Cell): number {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return (Math.abs(dq) + Math.abs(dq + dr) + Math.abs(dr)) / 2;
}

/** Distance from the generator. The single most-asked question in the game. */
export function fromOrigin(q: number, r: number): number {
  return (Math.abs(q) + Math.abs(q + r) + Math.abs(r)) / 2;
}

export function neighbours(q: number, r: number): Cell[] {
  return DIRECTIONS.map((d) => ({ q: q + d.q, r: r + d.r }));
}

/**
 * Every cell within `radius` of the origin, in a stable spiral order (centre
 * first, then ring by ring). Stable order matters: map generation walks this
 * list, so the same seed must always visit cells in the same sequence or two
 * peers would draw different maps from the same seed.
 */
export function disc(radius: number): Cell[] {
  const out: Cell[] = [{ q: 0, r: 0 }];
  for (let rad = 1; rad <= radius; rad++) {
    // Walk to the start of the ring, then round it.
    let q = -rad;
    let r = rad;
    for (let side = 0; side < 6; side++) {
      const d = DIRECTIONS[side];
      for (let step = 0; step < rad; step++) {
        out.push({ q, r });
        q += d.q;
        r += d.r;
      }
    }
  }
  return out;
}

/** Pointy-top axial -> pixel centre, for a hex of the given circumradius. */
export function toPixel(q: number, r: number, size: number): { x: number; y: number } {
  return {
    x: size * Math.sqrt(3) * (q + r / 2),
    y: size * 1.5 * r,
  };
}

/** Pixel -> axial, with proper cube rounding so hit-testing never lands between cells. */
export function fromPixel(x: number, y: number, size: number): Cell {
  const r = (y * 2) / 3 / size;
  const q = x / (size * Math.sqrt(3)) - r / 2;
  return round(q, r);
}

/** Round fractional axial coords to the nearest cell via cube rounding. */
export function round(qf: number, rf: number): Cell {
  const sf = -qf - rf;
  let q = Math.round(qf);
  let r = Math.round(rf);
  const s = Math.round(sf);
  const dq = Math.abs(q - qf);
  const dr = Math.abs(r - rf);
  const ds = Math.abs(s - sf);
  // Discard whichever coordinate moved furthest; the cube constraint q+r+s=0
  // then fixes it from the other two.
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  return { q, r };
}

/** Corner points of a pointy-top hex, for stroking a path. */
export function corners(cx: number, cy: number, size: number): Array<[number, number]> {
  const pts: Array<[number, number]> = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * (60 * i - 30);
    pts.push([cx + size * Math.cos(a), cy + size * Math.sin(a)]);
  }
  return pts;
}

/** Width and height in pixels of a disc of the given radius, at the given hex size. */
export function discExtent(radius: number, size: number): { w: number; h: number } {
  return {
    w: Math.sqrt(3) * size * (2 * radius + 1),
    h: 1.5 * size * 2 * radius + 2 * size,
  };
}
